import * as Ariakit from '@ariakit/react'
import * as RTooltip from '@radix-ui/react-tooltip'
import {init as sentryInit} from '@sentry/react'
import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import {Spinner, Toaster} from '@translated/babele-react'
import {HTTPError} from 'ky'
import {StrictMode} from 'react'
import ReactDOM from 'react-dom/client'
import {RouterProvider} from 'react-router'
import '@/services/i18n.ts'

import {envVars} from '@/envVars'
import {mswWorker} from '@/msw.browser'
import {makeRoutes} from '@/routes'

import '@/main.css'

if (envVars.VITE_SENTRY_DNS) {
  sentryInit({
    dsn: envVars.VITE_SENTRY_DNS,
  })
}

const retryFn = (count: number, err: unknown) => {
  if (err instanceof HTTPError) {
    if ([401, 403].includes(err.response.status)) {
      return false
    }
  }

  return count < 3
}

if (envVars.VITE_IS_MSW_ENABLED) {
  void mswWorker.start({
    onUnhandledRequest: (request, print) => {
      const requestUrl = new URL(request.url)
      if (
        /.(svg|ts|tsx|woff2|webmanifest|ico|png|scss|css|js|gif)$/.test(
          requestUrl.pathname,
        ) ||
        /(chrome-extension|sentry.io|mixpanel|clarity|hubspot|google|hscollectedforms|hubapi)/.test(
          requestUrl.href,
        ) ||
        request.headers.get('Accept')?.includes('image')
      ) {
        return
      }

      print.warning()
    },
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryFn,
      staleTime: 1000 * 10,
    },
    mutations: {
      retry: retryFn,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <RTooltip.Provider>
      <StrictMode>
        <RouterProvider
          fallbackElement={
            <div className="grid h-full place-items-center">
              <Ariakit.VisuallyHidden>
                Loading initial data...
              </Ariakit.VisuallyHidden>
              <Spinner />
            </div>
          }
          router={makeRoutes()}
        />
        <div className={'offer_toaster'}>
          <Toaster position={'top-right'} />
        </div>
      </StrictMode>
    </RTooltip.Provider>
    {envVars.VITE_IS_REACTQUERYDEVTOOLS_ENABLED && <ReactQueryDevtools />}
  </QueryClientProvider>,
)
